import type { FC } from "react";
import {
  Copy01Icon,
  MailSend01Icon,
  ViewIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { EmailPillsInput } from "shared/components/EmailPillsInput";
import type { OwnerReportSettings } from "api/owner-report-settings";
import { MAX_OWNER_RECIPIENTS } from "api/owner-report-shares";
import { ReportScopeTiles } from "./ReportScopeTiles";

export const ShareTab: FC<{
  settings: OwnerReportSettings;
  emails: string[];
  draft: string;
  message: string;
  isSending: boolean;
  isCopying: boolean;
  canSubmit: boolean;
  onEmailsChange: (emails: string[]) => void;
  onDraftChange: (draft: string) => void;
  onMessageChange: (message: string) => void;
  onPreview: () => void;
  onCopyLink: () => void;
  onSend: () => void;
}> = ({
  settings,
  emails,
  draft,
  message,
  isSending,
  isCopying,
  canSubmit,
  onEmailsChange,
  onDraftChange,
  onMessageChange,
  onPreview,
  onCopyLink,
  onSend
}) => (
  // Two parts: a body that scrolls and an action row that does not. The row used
  // to be the last block of one long scrolling panel, so on a short viewport the
  // dialog opened with Preview, Copy link and Send to owner all below the fold.
  // The negative margins let the scroll edge run to the panel's own padding
  // rather than leaving a gutter the content stops short of.
  <div className="flex min-h-0 flex-1 flex-col">
    <div className="-mx-6 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 pb-6 pt-6">
      <div className="flex flex-col gap-2">
        <label
          htmlFor="owner-share-emails"
          className="text-sm font-medium text-content-1000"
        >
          Owner email
        </label>
        <EmailPillsInput
          id="owner-share-emails"
          emails={emails}
          draft={draft}
          onDraftChange={onDraftChange}
          onChange={onEmailsChange}
          placeholder="owner@example.com"
          addMorePlaceholder="Add another owner"
          maxEmails={MAX_OWNER_RECIPIENTS}
        />
        {/* One link for the whole share, so say so: whoever holds it can pass it
            on, and the partner should know that before they type a second name. */}
        <p className="text-xs text-content-400">
          Up to {MAX_OWNER_RECIPIENTS} owners share one private link that
          expires in {settings.sharing.link_expiry_days} days.
        </p>
      </div>

      {/* Hidden outright when the partner has turned messages off, rather than
          shown disabled — there is nothing here for the sender to act on. */}
      <Show if={settings.sharing.allow_message}>
        <div className="flex flex-col gap-2">
          <label
            htmlFor="owner-share-message"
            className="text-sm font-medium text-content-1000"
          >
            Message{" "}
            <span className="font-normal text-content-400">(optional)</span>
          </label>
          <textarea
            id="owner-share-message"
            value={message}
            onChange={(event) => onMessageChange(event.target.value)}
            rows={4}
            // Matches the API's cap, so a long note is stopped here rather than
            // coming back as a 400 after the partner has written it.
            maxLength={1000}
            placeholder="Add a note for the owner"
            className="w-full resize-y rounded-lg border border-alpha-80 bg-background-1000 px-3 py-2 text-base text-content-1000 placeholder:text-content-200 focus:border-primary-500 focus:outline-none"
          />
        </div>
      </Show>

      <ReportScopeTiles settings={settings} />
    </div>

    <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-alpha-40 pt-5">
      <Button
        variant={ButtonVariant.BASE}
        size={ButtonSize.SM}
        onClick={onPreview}
      >
        <span className="flex items-center gap-2">
          <HugeiconsIcon icon={ViewIcon} size={16} />
          Preview
        </span>
      </Button>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          onClick={onCopyLink}
          disabled={!canSubmit || isSending}
          loading={isCopying}
        >
          <span className="flex items-center gap-2">
            <HugeiconsIcon icon={Copy01Icon} size={16} />
            Copy link
          </span>
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.SM}
          onClick={onSend}
          disabled={!canSubmit || isCopying}
          loading={isSending}
        >
          <span className="flex items-center gap-2">
            <HugeiconsIcon icon={MailSend01Icon} size={16} />
            Send to owner
          </span>
        </Button>
      </div>
    </div>
  </div>
);
